import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { isPartLocationRun } from "../LocationRun/anchor";
import type { LocationRun } from "../LocationRun/runs";
import {
  branchForSiteRun,
  siteRunFollowsCastColumn,
} from "../LocationRun/siteAnchor";
import { partRunPhase, partStaySegments } from "../../rail/PartRunPhase/phase";
import type { LocationPlacementSegment } from "./plan";

function boundariesForRun(
  layout: BookTimelineLayout,
  run: LocationRun,
): number[] {
  const times = new Set<number>([
    0,
    layout.totalDurationMs,
    run.startMs,
    run.endMs,
  ]);
  if (isPartLocationRun(run)) {
    for (const span of partStaySegments(layout.rail, run)) {
      times.add(span.startMs);
      times.add(span.endMs);
    }
  } else {
    const branch = branchForSiteRun(run, layout.rail);
    if (branch !== undefined) {
      times.add(branch.startMs);
      times.add(branch.endMs);
    }
  }
  return [...times]
    .filter((ms) => ms >= 0 && ms <= layout.totalDurationMs)
    .sort((a, b) => a - b);
}

function segmentAt(
  layout: BookTimelineLayout,
  run: LocationRun,
  playheadMs: number,
): LocationPlacementSegment {
  if (!isPartLocationRun(run)) {
    const onCast = siteRunFollowsCastColumn(layout.rail, run, playheadMs);
    return {
      startMs: playheadMs,
      endMs: playheadMs,
      mode: onCast ? "cast-column" : "rail-slot",
      castRole: onCast ? "primary" : undefined,
    };
  }
  const phase = partRunPhase(layout.rail, run, playheadMs);
  if (phase.kind === "active") {
    return {
      startMs: playheadMs,
      endMs: playheadMs,
      mode: "cast-column",
      castRole: "secondary",
    };
  }
  return {
    startMs: playheadMs,
    endMs: playheadMs,
    mode: "rail-slot",
  };
}

function sameSegment(
  a: LocationPlacementSegment,
  b: LocationPlacementSegment,
): boolean {
  return a.mode === b.mode && a.castRole === b.castRole;
}

export function buildRunSegments(
  layout: BookTimelineLayout,
  run: LocationRun,
): LocationPlacementSegment[] {
  const bounds = boundariesForRun(layout, run);
  const merged: LocationPlacementSegment[] = [];
  for (let index = 0; index < bounds.length - 1; index++) {
    const startMs = bounds[index];
    const endMs = bounds[index + 1];
    if (endMs <= startMs) {
      continue;
    }
    const sample = segmentAt(layout, run, startMs);
    const piece: LocationPlacementSegment = {
      startMs,
      endMs,
      mode: sample.mode,
      castRole: sample.castRole,
    };
    const last = merged[merged.length - 1];
    if (last !== undefined && sameSegment(last, piece)) {
      last.endMs = endMs;
    } else {
      merged.push(piece);
    }
  }
  return merged;
}
